import { NgClass } from '@angular/common';
import { Component, inject, type OnDestroy, type OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ButtonGroupModule } from '@openng/optimus-ui/buttongroup';
import { ChipModule } from '@openng/optimus-ui/chip';
import { ConfirmPopupModule } from '@openng/optimus-ui/confirmpopup';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MultiSelectModule } from '@openng/optimus-ui/multiselect';
import { PanelModule } from '@openng/optimus-ui/panel';
import { PopoverModule } from '@openng/optimus-ui/popover';
import { SelectModule } from '@openng/optimus-ui/select';
import { TagModule } from '@openng/optimus-ui/tag';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { injectParams } from 'ngxtension/inject-params';
import { concatMap, of } from 'rxjs';

import { MarkdownLazyModule } from '@app/markdown-lazy.module';
import {
    getRiskAssessmentForAPI,
    getRiskAssessmentForm,
    parseRiskAssessmentConfig,
    RISK_MITIGATION_METHOD,
    type Risk,
    type RiskAssessmentAdapter,
    type RiskAssessmentFormGroup,
    type RiskCategory,
} from '@app/modules/management/models/risk-assessment.model';
import { RiskAssessmentService } from '@app/modules/management/services/risk-assessment.service';
import { DescriptionComponent } from '@app/shared/components/description/description.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { BaseDetails } from '@app/shared/components/registry/base-details';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { ACTIONS, OBJECTS, SKELETON_TYPES } from '@app/shared/constants/shared.constant';
import { IncludesPipe } from '@app/shared/pipes/includes.pipe';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import { RouterHelper } from '@app/shared/utils/router-helper';

export const RISK_MITIGATION_DETAILS: {
    [key in RISK_MITIGATION_METHOD]: {
        name: string;
        description: string;
    };
} = {
    [RISK_MITIGATION_METHOD.NOT_APPLICABLE]: {
        name: 'Not Applicable',
        description:
            'Risk is not applicable based on model design / eliminated by model design choice',
    },
    [RISK_MITIGATION_METHOD.INPUT_GUARDRAILS]: {
        name: 'Input Guardrails',
        description:
            'Guardrails were implemnted on the input before the user messages were sent to the LLM',
    },
    [RISK_MITIGATION_METHOD.PROMPT_INSTRUCTIONS]: {
        name: 'Prompt Instructions',
        description: 'Explicit Instructions were added in the Prompt to reduce risk from the LLM',
    },
    [RISK_MITIGATION_METHOD.OUTPUT_GUARDRAILS]: {
        name: 'Output Guardrail',
        description: 'Guardrails were implemented on the output of the LLM response',
    },
    [RISK_MITIGATION_METHOD.OTHER]: {
        name: 'Other',
        description: 'Specify a custom or alternative risk mitigation method',
    },
};

@Component({
    selector: 'cor-risk-assessment-details',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        SkeletonComponent,
        TooltipModule,
        ConfirmPopupModule,
        ButtonModule,
        TagModule,
        PopoverModule,
        NgClass,
        SelectModule,
        PanelModule,
        MarkdownLazyModule,
        ButtonGroupModule,
        AvatarModule,
        MultiSelectModule,
        InputTextModule,
        ChipModule,
        InputContainerComponent,
        DescriptionComponent,
        IncludesPipe,
    ],
    templateUrl: './risk-assessment-details.component.html',
    host: {
        class: 'tw:flex tw:flex-col tw:h-full',
    },
})
export class RiskAssessmentDetailsComponent
    extends BaseDetails<RiskAssessmentAdapter, RiskAssessmentFormGroup>
    implements OnInit, OnDestroy
{
    protected readonly currentProject = injectParams<string>('projectSlug');
    private readonly service = inject(RiskAssessmentService);
    protected readonly toast = inject(ToastNotificationService);
    private readonly router = inject(Router);

    protected readonly SKELETON_TYPES = SKELETON_TYPES;
    protected readonly ACTIONS = ACTIONS;
    protected readonly object = OBJECTS.RISK_ASSESSMENT;
    protected readonly TAB_ROUTES = TAB_ROUTES;
    protected readonly RISK_MITIGATION_METHOD = RISK_MITIGATION_METHOD;
    protected readonly RISK_MITIGATION_DETAILS = RISK_MITIGATION_DETAILS;

    protected readonly item = this.service.item;
    protected readonly riskCategories = signal<RiskCategory[]>([]);
    protected readonly riskMitigationMethods = signal(
        Object.entries(RISK_MITIGATION_DETAILS).map(([id, value]) => ({
            id: id as RISK_MITIGATION_METHOD,
            ...value,
        })),
    );
    /**
     * selectedRisk tracks which risk in every category is visible to the user
     * The key is RiskCategory.key. The value is the Risk <name, key, description>
     */
    protected readonly selectedRisk = signal<Record<string, Risk>>(null);

    ngOnInit() {
        RouterHelper.unsavedChangesGuard(this.router, this.destroyRef, () => this.canDeactivate());
        this.registryService.saveRequested$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(info => {
                // FIXME: this is a temporary fix to allow saving of risk assessment when saved inside pipeline tab.
                if (info.object === this.object || info.object === OBJECTS.PIPELINE)
                    this.onSaveClick(info);
            });

        const riskCategories = parseRiskAssessmentConfig(this.item()?.config);
        this.riskCategories.set(riskCategories);
        this.selectedRisk.set(
            Object.fromEntries(
                riskCategories.map(riskCategory => [riskCategory.key, riskCategory.risks[0]]),
            ),
        );
        this.initForm();
        this.registryService.tabForm.set(this.form());
        this.registerEditEvent();
    }

    ngOnDestroy() {
        this.registryService.reset();
    }

    private initForm() {
        this.form.set(getRiskAssessmentForm(this.item(), this.riskCategories()));
    }

    canDeactivate() {
        return !this.form()?.dirty;
    }

    protected onSaveClick(snapshotInfo: { versionName?: string; comment?: string }): void {
        if (this.form().invalid) {
            FormHelper.showFormErrors(this.form(), this.toast);
            return;
        }

        this.registryService.isSaving.set(true);

        const payload = getRiskAssessmentForAPI({
            ...this.form().getRawValue(),
            comment: snapshotInfo.comment,
            versionName: snapshotInfo.versionName,
            projectSlug: this.item()?.id ? this.item()?.projectSlug : this.currentProject(),
        });

        const isUpdate = Boolean(this.item()?.id);
        (isUpdate ? this.service.update(payload) : this.service.create(payload))
            .pipe(
                concatMap(response => this.postSaveItem(response.result)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    this.toast.showSuccess(
                        isUpdate
                            ? 'Risk Assessment updated successfully'
                            : 'Risk Assessment created successfully',
                    );
                    FormHelper.markFormAsPristine(this.form());
                },
                error: err => {
                    this.apiErrorService.handleValidationError(err, this.form());
                },
            })
            .add(() => this.registryService.isSaving.set(false));
    }

    private postSaveItem(item: RiskAssessmentAdapter) {
        // Appending actions from the initial item api call
        this.item.set(this.item()?.id ? { ...item, actions: this.item()?.actions } : item);

        this.form().patchValue(
            getRiskAssessmentForm(this.item(), this.riskCategories()).getRawValue(),
            {
                emitEvent: false,
            },
        );

        // on successful save, mark form as pristine to avoid discard changes confirmation box
        FormHelper.markFormAsPristine(this.form());

        return of(null);
    }

    protected onRiskSelectionChange(risk: Risk, categoryKey: string) {
        if (risk) {
            this.selectedRisk.update(current => ({ ...current, [categoryKey]: risk }));
        }
    }
}
